<template>
  <div ref="container" />
  <div v-if="!hideActions" class="d-flex justify-end mt-3">
    <v-btn
      color="primary"
      :loading="busy"
      :disabled="!ready || disabled"
      @click="confirm"
    >
      Confirm card payment
    </v-btn>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import type {
  Appearance,
  StripeElements,
  StripePaymentElement,
} from "@stripe/stripe-js";
import { useTheme } from "vuetify";
import { useStripe } from "@/composables/useStripe";
import { useNotificationStore } from "@/stores/notification";

const props = defineProps<{
  clientSecret: string;
  disabled: boolean;
  hideActions?: boolean;
  /** Customer session secret loader; enables saving and reusing the payer's cards. */
  customerSession?: () => Promise<string | null | undefined>;
}>();
const emit = defineEmits<{ confirmed: []; processing: [value: boolean] }>();
const container = ref<HTMLElement | null>(null);
const ready = ref(false);
const busy = ref(false);
const stripe = useStripe();
const notification = useNotificationStore();
let elements: StripeElements | undefined;
let element: StripePaymentElement | undefined;
let disposed = false;
const theme = useTheme();
// Match the card form to the active Vuetify theme. Variables alone leave Stripe's
// accordion/tab containers white in dark mode, so those surfaces are set explicitly.
const appearance = computed<Appearance>(() => {
  const colors = theme.current.value.colors;
  const muted = colors["on-surface"];
  return {
    theme: "flat",
    variables: {
      colorPrimary: colors.primary,
      colorBackground: colors.surface,
      colorText: colors["on-surface"],
      colorTextSecondary: muted,
      colorTextPlaceholder: muted,
      colorDanger: colors.error,
      fontFamily: "Roboto, sans-serif",
      borderRadius: "4px",
    },
    rules: {
      ".AccordionItem": {
        backgroundColor: colors.surface,
        borderColor: colors.background,
        boxShadow: "none",
      },
      ".AccordionItem--selected, .TabLabel--selected, .TabIcon--selected": {
        color: colors["on-surface"],
        fill: colors["on-surface"],
      },
      ".Tab, .Block, .PickerItem": {
        backgroundColor: colors.surface,
        borderColor: colors.background,
        boxShadow: "none",
      },
      ".Input": {
        backgroundColor: colors.background,
        color: colors["on-surface"],
        boxShadow: "none",
      },
      ".Input:focus": {
        boxShadow: `0 0 0 1px ${colors.primary}`,
      },
      ".Label": { color: muted },
      // The flat theme draws the "save card" checkbox borderless, which vanishes on dark surfaces.
      ".CheckboxInput": {
        backgroundColor: colors.background,
        borderColor: colors["on-surface"],
        borderStyle: "solid",
        borderWidth: "2px",
        boxShadow: "none",
      },
      ".CheckboxInput--checked": {
        backgroundColor: colors.primary,
        borderColor: colors.primary,
      },
      ".CheckboxInput:focus": {
        boxShadow: `0 0 0 2px ${colors.primary}`,
      },
      ".CheckboxLabel": { color: colors["on-surface"] },
    },
  };
});
watch(appearance, (value) => elements?.update({ appearance: value }));
defineExpose({
  confirm,
  canConfirm: computed(() => ready.value && !busy.value && !props.disabled),
});

onMounted(async () => {
  try {
    const [, customerSessionClientSecret] = await Promise.all([
      stripe.load(),
      loadCustomerSession(),
    ]);
    if (disposed) return;
    if (!stripe.stripe.value || !container.value)
      throw new Error("Could not initialize Stripe.");
    elements = stripe.stripe.value.elements({
      clientSecret: props.clientSecret,
      appearance: appearance.value,
      fonts: [
        { cssSrc: "https://fonts.googleapis.com/css?family=Roboto:400,500" },
      ],
      ...(customerSessionClientSecret ? { customerSessionClientSecret } : {}),
    });
    element = elements.create("payment");
    element.on("ready", () => {
      ready.value = true;
    });
    element.on("loaderror", ({ error }) => {
      notification.addSnackbar(
        error.message ?? "Could not load the card form.",
        "error",
      );
    });
    element.mount(container.value);
  } catch (error) {
    notification.addSnackbar(
      error instanceof Error ? error.message : "Could not load the card form.",
      "error",
    );
  }
});

// Saved cards are optional; the plain card form still works without a session.
async function loadCustomerSession() {
  try {
    return (await props.customerSession?.()) ?? undefined;
  } catch {
    return undefined;
  }
}

async function confirm() {
  if (!elements || !stripe.stripe.value || busy.value || props.disabled) return;
  busy.value = true;
  emit("processing", true);
  let confirmed = false;
  try {
    const result = await stripe.stripe.value.confirmPayment({
      elements,
      confirmParams: { return_url: window.location.href },
      redirect: "if_required",
    });
    if (result.error)
      throw new Error(result.error.message ?? "Card confirmation failed.");
    confirmed = true;
  } catch (error) {
    notification.addSnackbar(
      error instanceof Error ? error.message : "Card confirmation failed.",
      "error",
    );
  } finally {
    busy.value = false;
    emit("processing", false);
  }
  if (confirmed && !disposed) emit("confirmed");
}

onBeforeUnmount(() => {
  disposed = true;
  emit("processing", false);
  element?.destroy();
});
</script>
